import _extends from "@babel/runtime/helpers/extends";
import { Box, Collapse, Divider } from '@wix/design-system';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { useCallback, useContext, useMemo } from 'react';
import { wdsStickyHeaderSideShadow, wdsThemeColors } from '../../../../utils/wds-theme-colors';
import { SubtitleLayout } from '../../types';
import { ArrowIcon } from '../arrowIcon/arrow-icon';
import { CollapseContext } from '../collapse-list/collapse-list';
import { useScrollIntoView } from '../collapse-list/scroll-into-view';
const DefaultWrapper = /*#__PURE__*/React.memo(_ref => {
  let {
    children
  } = _ref;
  return /*#__PURE__*/React.createElement(Box, {
    display: "block"
  }, children);
});
const HEADER_PADDING = {
  large: '15px 24px',
  medium: '16px',
  small: '12px 16px'
};
const TOP_SHADOW = '0px 8px 6px -6px rgba(0, 6, 36, 0.05) inset, 0px 22px 18px -18px rgba(0, 6, 36, 0.1) inset';
const BOTTOM_SHADOW = '0px -8px 6px -6px rgba(0, 6, 36, 0.05) inset, 0px -22px 18px -18px rgba(0, 6, 36, 0.1) inset';
const stickyBorderColorVar = '--collapse-list-sticky-border-color';
const stickyBorderColor = `var(${stickyBorderColorVar})`;

// Helper components for different layout strategies
const TitleSuffixRow = /*#__PURE__*/React.memo(_ref2 => {
  let {
    title,
    suffix,
    suffixColor
  } = _ref2;
  return /*#__PURE__*/React.createElement(Box, {
    align: "space-between",
    direction: "horizontal",
    gap: "SP3",
    verticalAlign: "top"
  }, /*#__PURE__*/React.createElement(Box, {
    minWidth: 0
  }, title), /*#__PURE__*/React.createElement(Box, {
    color: suffixColor,
    direction: "horizontal",
    flexShrink: 0,
    gap: "SP3",
    verticalAlign: "middle"
  }, suffix));
});
const InlineContentLayout = /*#__PURE__*/React.memo(_ref3 => {
  let {
    title,
    subtitle,
    suffix,
    suffixColor
  } = _ref3;
  return /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    flexGrow: 1,
    minWidth: 0,
    paddingTop: subtitle ? '2px' : undefined
  }, /*#__PURE__*/React.createElement(TitleSuffixRow, {
    suffix: suffix,
    suffixColor: suffixColor,
    title: title
  }), subtitle);
});
const GroupedContentLayout = /*#__PURE__*/React.memo(_ref4 => {
  let {
    title,
    subtitle,
    suffix,
    suffixColor
  } = _ref4;
  return /*#__PURE__*/React.createElement(Box, {
    align: "space-between",
    direction: "horizontal",
    flexGrow: 1,
    gap: "SP3",
    minWidth: 0,
    verticalAlign: "top"
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    minWidth: 0
  }, title, subtitle), /*#__PURE__*/React.createElement(Box, {
    color: suffixColor,
    direction: "horizontal",
    flexShrink: 0,
    gap: "SP3",
    verticalAlign: "middle"
  }, suffix));
});
export const CollapseListItem = /*#__PURE__*/React.memo(_ref5 => {
  let {
    id,
    title,
    wrapper,
    variant = 'collapsible',
    disabled = false,
    dataHook,
    onToggle,
    onButtonClick,
    showContentDivider = false,
    prefix,
    subtitle,
    suffix,
    children,
    headerPadding = 'small',
    showTopShadow = false,
    showBottomShadow = false,
    showArrowIcon = true,
    subtitleLayout = SubtitleLayout.Grouped,
    stickyHeader = false,
    showStickyBorder = false,
    showStickyHeaderSideShadow = true,
    onCollapseAnimationEnd
  } = _ref5;
  const atoms = useContext(CollapseContext);
  if (!atoms) {
    throw new Error('CollapseListItem must be inside CollapseList.');
  }
  const {
    headerRef,
    contentRef,
    scrollWithDelay
  } = useScrollIntoView();
  const {
    toggleItemAtom,
    isItemOpenAtom
  } = atoms;
  const toggleItem = useSetAtom(toggleItemAtom);
  const isExpanded = useAtomValue(isItemOpenAtom(id));
  const handleClick = useCallback(() => {
    if (disabled) {
      return;
    }
    if (variant === 'collapsible') {
      toggleItem(id);
      onToggle == null || onToggle(id, !isExpanded);
    } else if (variant === 'button') {
      onButtonClick == null || onButtonClick(id);
    }
  }, [disabled, variant, toggleItem, id, onToggle, isExpanded, onButtonClick]);
  const boxShadow = useMemo(() => {
    if (showTopShadow && showBottomShadow) {
      return `${TOP_SHADOW}, ${BOTTOM_SHADOW}`;
    }
    if (showTopShadow) {
      return TOP_SHADOW;
    }
    if (showBottomShadow) {
      return BOTTOM_SHADOW;
    }
    return 'none';
  }, [showTopShadow, showBottomShadow]);
  const Wrapper = wrapper || DefaultWrapper;
  const headerStickyStyles = stickyHeader ? {
    backgroundColor: wdsThemeColors.surfaceTertiary,
    position: 'sticky',
    top: 0,
    zIndex: 2,
    ...(showStickyBorder && {
      border: `1px solid ${stickyBorderColor}`,
      borderBottom: isExpanded ? 'none' : `1px solid ${stickyBorderColor}`,
      borderRadius: isExpanded ? '8px 8px 0 0' : '8px',
      ...(showStickyHeaderSideShadow && {
        boxShadow: wdsStickyHeaderSideShadow
      }),
      [stickyBorderColorVar]: wdsThemeColors.borderDarkSecondary
    })
  } : {};
  const contentStickyStyles = stickyHeader && showStickyBorder && isExpanded ? {
    borderLeft: `1px solid ${stickyBorderColor}`,
    borderRadius: '0 0 8px 8px',
    borderRight: `1px solid ${stickyBorderColor}`,
    style: {
      [stickyBorderColorVar]: wdsThemeColors.borderDarkSecondary
    }
  } : undefined;
  const actionIconColor = disabled ? wdsThemeColors.iconDisabled : wdsThemeColors.iconPrimary;
  const shouldUseVisibleOverflow = stickyHeader || isExpanded;
  return /*#__PURE__*/React.createElement(Wrapper, null, /*#__PURE__*/React.createElement(Box, {
    dataHook: "collapse-list-item",
    direction: "vertical",
    overflow: shouldUseVisibleOverflow ? 'visible' : 'hidden',
    position: "relative",
    zIndex: 1
  }, /*#__PURE__*/React.createElement("div", {
    "aria-disabled": disabled,
    "aria-expanded": isExpanded,
    "data-hook": dataHook,
    ref: headerRef,
    role: "button",
    style: {
      borderRadius: '8px',
      cursor: disabled ? 'default' : 'pointer',
      ...headerStickyStyles
    },
    tabIndex: 0,
    onClick: handleClick,
    onKeyDown: e => {
      if (['Enter', ' '].includes(e.key)) {
        e.stopPropagation();
        handleClick();
      }
    }
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "horizontal",
    gap: "SP3",
    padding: HEADER_PADDING[headerPadding],
    verticalAlign: "top"
  }, prefix, subtitleLayout === SubtitleLayout.Grouped ? /*#__PURE__*/React.createElement(GroupedContentLayout, {
    subtitle: subtitle,
    suffix: suffix,
    suffixColor: actionIconColor,
    title: title
  }) : /*#__PURE__*/React.createElement(InlineContentLayout, {
    subtitle: subtitle,
    suffix: suffix,
    suffixColor: actionIconColor,
    title: title
  }), showArrowIcon && /*#__PURE__*/React.createElement(ArrowIcon, {
    disabled: disabled,
    isExpanded: isExpanded,
    variant: variant
  }))), variant === 'collapsible' && /*#__PURE__*/React.createElement(Collapse, {
    open: isExpanded,
    unmountOnExit: true,
    onCollapseAnimationEnd: onCollapseAnimationEnd,
    onExpandAnimationEnd: scrollWithDelay
  }, showContentDivider && /*#__PURE__*/React.createElement(Divider, {
    direction: "horizontal"
  }), /*#__PURE__*/React.createElement(Box, _extends({
    boxShadow: boxShadow,
    display: "block"
  }, contentStickyStyles), /*#__PURE__*/React.createElement("div", {
    ref: contentRef
  }, children)))));
});
//# sourceMappingURL=collapse-list-item.js.map